import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  AuthUser,
  fetchMFAPreference,
  fetchUserAttributes,
  FetchUserAttributesOutput,
  getCurrentUser,
  signIn,
  SignInInput,
  SignInOutput,
  signOut,
} from 'aws-amplify/auth';

import { TAuthState } from '@/shared/redux/api/auth/auth-api.types';
import { clearCachedToken } from '@/shared/redux/api/utils/auth';
import { LoadingStateEnum } from '@/shared/types/common.types';

export const login = createAsyncThunk<
  SignInOutput,
  SignInInput,
  { rejectValue: string }
>('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const { isSignedIn, nextStep } = await signIn(credentials);
    return { isSignedIn, nextStep };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const fetchCurrentUser = createAsyncThunk<
  {
    user: AuthUser;
    attributes: FetchUserAttributesOutput;
    mfaEnabled: boolean;
  },
  void,
  { rejectValue: string }
>('auth/fetchCurrentUser', async (_, { rejectWithValue }) => {
  try {
    const user = await getCurrentUser();
    const attributes = await fetchUserAttributes();
    const mfaPreference = await fetchMFAPreference();
    const mfaEnabled = (mfaPreference?.enabled ?? []).length > 0;

    return { user, attributes, mfaEnabled };
  } catch (error) {
    return rejectWithValue((error as Error).message);
  }
});

export const logout = createAsyncThunk<void, void, { rejectValue: string }>(
  'auth/logout',
  async (_, { rejectWithValue }) => {
    try {
      clearCachedToken();
      await signOut({ global: true });

      const i18nextLng = localStorage.getItem('i18nextLng');
      const theme = localStorage.getItem('theme');

      localStorage.clear();

      if (i18nextLng) {
        localStorage.setItem('i18nextLng', i18nextLng);
      }
      if (theme) {
        localStorage.setItem('theme', theme);
      }

      window.location.reload();
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  },
);

const initialState: TAuthState = {
  isAuthenticated: false,
  user: null,
  error: null,
  status: LoadingStateEnum.IDLE,
  signInStep: null,
  backofficeTenantIdToken: null,
  maintenanceAlertOpen: false,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    setBackofficeTenantIdToken: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.backofficeTenantIdToken = action.payload;
    },
    setMaintenanceAlertOpen: (state, action: PayloadAction<boolean>) => {
      state.maintenanceAlertOpen = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        login.fulfilled,
        (state, action: PayloadAction<SignInOutput>) => {
          state.isAuthenticated = action.payload.isSignedIn;
          state.signInStep = action.payload.nextStep?.signInStep;
        },
      )
      .addCase(login.rejected, (state, action) => {
        state.error = action.payload ?? 'Login failed';
      })
      .addCase(fetchCurrentUser.pending, (state) => {
        state.status = LoadingStateEnum.LOADING;
      })
      .addCase(
        fetchCurrentUser.fulfilled,
        (
          state,
          action: PayloadAction<{
            user: AuthUser;
            attributes: FetchUserAttributesOutput;
            mfaEnabled: boolean;
          }>,
        ) => {
          state.status = LoadingStateEnum.DONE;
          state.user = {
            ...action.payload.user,
            // Backoffice and Console authenticate against separate Cognito
            // pools, so only one of these attributes is ever present: super
            // users carry `custom:superUserId`, tenant users `custom:userId`.
            userId:
              action.payload.attributes['custom:superUserId'] ??
              action.payload.attributes['custom:userId']!,
            attributes: action.payload.attributes,
            mfaEnabled: action.payload.mfaEnabled,
          };
        },
      )
      .addCase(fetchCurrentUser.rejected, (state, action) => {
        state.status = LoadingStateEnum.DONE;
        state.user = null;
        state.error = action.payload ?? 'Failed to fetch user';
      })
      .addCase(logout.fulfilled, () => initialState);
  },
});

export default authSlice.reducer;

export const selectUser = (state: { auth: TAuthState }) => state.auth.user;
export const selectAuthStatus = (state: { auth: TAuthState }) =>
  state.auth.status;
export const selectBackofficeTenantIdToken = (state: { auth: TAuthState }) =>
  state.auth.backofficeTenantIdToken;
export const selectMaintenanceAlertOpen = (state: { auth: TAuthState }) =>
  state.auth.maintenanceAlertOpen;

export const { setBackofficeTenantIdToken, setMaintenanceAlertOpen } =
  authSlice.actions;
